import { cloneDeep, isString } from 'lodash';
import { isHotKeyPress } from '@/utils/event-kit';
import { getCenterPointForOrigin, getGroupLayerProps, getLayerViewNode, getNewCenterPoint } from '@/utils/layer-kit';
import { getActualAngle, rotatePointWithFocus } from '@/utils/math-kit';
import {
    BaseLayerViewType,
    DirectionType,
    IBaseOperateFlag,
    ICorrectLayerMinSize,
    ILayerOriginInfo,
    ILayerProps,
    IPoint,
    IResizeUpdate,
    ISize,
    LAYER_MIN_SIZE,
    LayerType,
    ScaleInfo,
} from '@/version-types';
import { CoreAPI, getBehavior } from '../../core';
import { getFramework } from '../../instance';

/**
 * 获取图层视图需要移动的节点
 */
export function updateLayerTransform(identity, layer) {
    const dom = getLayerViewNode(identity, layer.id);
    if (!dom) return;
    const doms = [dom];
    // 图片图层会有同级的effect节点  div[data-layer-id="${layerId}_effect"]
    const domAttrId = dom.getAttribute('data-layer-id');
    const effectAttrId =
        domAttrId && domAttrId.indexOf('-parent') > -1 ? `${layer.id}_effect-parent` : `${domAttrId}_effect`;
    const effectDom = dom.parentNode?.querySelector<HTMLElement>(`div[data-layer-id="${effectAttrId}"]`);
    if (effectDom) {
        doms.push(effectDom);
    }
    for (const dom of doms) {
        if (dom) {
            dom.style.transform = `translate(${layer.left}px, ${layer.top}px) rotate(${layer.rotate}deg)`;
        }
    }
}

/**
 * 缩放图层
 */
export function updateLayerScale(
    identity: string,
    layerId: string,
    offsetX: number,
    offsetY: number,
    beforeScaleLayerSize: ISize,
    direction: DirectionType,
    reverse: boolean,
    originInfo,
    childOriginInfoMap,
    args: IBaseOperateFlag,
    keepRatio?: boolean,
) {
    const isStart = args.isStart;
    const framework = getFramework(identity);
    const layer = framework.getLayer(layerId, true);
    const flatLayersMap = framework.getFlatLayersMap();
    const scaleInfo = {
        identity,
        ...getScaleInfo(identity, flatLayersMap, originInfo, childOriginInfoMap, offsetX),
        originalOffsetX: offsetX,
        offsetY,
        beforeScaleLayerSize,
        keepRatio,
    };
    execScale(identity, layer, scaleInfo, originInfo, childOriginInfoMap, direction, reverse, isStart, false, (layer) =>
        flatLayersMap.set(layer.id, layer, true, layer.id === layerId),
    );
    framework.rerender();
    framework.events.emitBaseOperate(layerId, BaseLayerViewType.SCALE, args, {
        layerId,
        offsetX,
        offsetY,
        direction,
        reverse,
        keepRatio,
        beforeScaleLayerSize,
    });
    return {
        width: layer.width,
        height: layer.height,
        isMin: scaleInfo.isMin,
    };
}

/**
 * 更新图层尺寸
 */
export function updateLayerResize(
    identity: string,
    layerId: string,
    offset: number,
    direction: DirectionType,
    reverse: boolean,
    isChangeHeight: boolean,
    originInfo,
    childOriginInfoMap,
    args: IBaseOperateFlag,
) {
    const isStart = args.isStart;
    const framework = getFramework(identity);
    const layer = framework.getLayer(layerId, true);
    const flatLayersMap = framework.getFlatLayersMap();
    const previousTickLayer = cloneDeep(layer);
    let resizeResult: IResizeUpdate | undefined | void;

    // 统一处理图层尺寸和位置
    const processLayer = (layer: ILayerProps, originInfo: ILayerOriginInfo, offsetChange: number) => {
        const { width: originWidth, height: originHeight } = originInfo;

        const minWidth = originInfo.cutWidth;
        const minHeight = originInfo.cutHeight;

        // 根据实际改变值调整尺寸
        if (isChangeHeight) {
            layer.height = Math.max(minHeight, Math.ceil(originHeight + offsetChange));
        } else {
            layer.width = Math.max(minWidth, Math.ceil(originWidth + offsetChange));
        }

        const res = getBehavior(identity, layer)?.onBeforeResizeUpdate?.(layer, {
            isChangeHeight,
            previousTickLayer,
            isStart,
            originInfo,
            reverse,
        });

        // 只存储顶层图层的resize结果
        if (layer.id === layerId) {
            resizeResult = res;
        }

        // 计算新的位置
        const finalLayer = res || layer;
        const newCenter = isChangeHeight
            ? getNewCenterPoint(originInfo, finalLayer, reverse, 1)
            : getNewCenterPoint(originInfo, finalLayer, reverse, direction);

        layer.left = newCenter.x - finalLayer.width / 2;
        layer.top = newCenter.y - finalLayer.height / 2;

        flatLayersMap.set(layer.id, layer, true, layer.id === layerId);

        if (!res?.skipChild) {
            // 处理子图层，传递实际的尺寸变化值
            const { children } = layer;
            (children || []).forEach((item) => {
                const value = childOriginInfoMap.get(item.id);
                if (!value) return;
                processLayer(item, value, offsetChange);
            });
        }
    };

    // 执行处理
    processLayer(layer, originInfo, offset);

    let isMin = false;
    // 检查最小尺寸
    if (resizeResult?.isMin) {
        isMin = true;
    } else {
        if (isChangeHeight) {
            isMin = layer.height <= originInfo.cutHeight;
        } else {
            isMin = layer.width <= originInfo.cutWidth;
        }
    }
    framework.rerender();
    framework.events.emitBaseOperate(layerId, BaseLayerViewType.RESIZE, args, {
        layerId,
        offset,
        direction,
        reverse,
        isChangeHeight,
    });
    return { isMin, width: layer.width, height: layer.height };
}

/**
 * 旋转图层
 */
export function updateLayerRotate(identity, layerId, angle, originInfo, childOriginInfoMap, args: IBaseOperateFlag) {
    const rotate = 0;
    const framework = getFramework(identity);
    const flatLayersMap = framework.getFlatLayersMap();

    const layer = framework.getLayer(layerId);
    if (!layer) return rotate;

    execRotate(
        layer,
        originInfo,
        childOriginInfoMap,
        angle,
        (layer) => {
            updateLayerTransform(identity, layer);
            flatLayersMap.set(layer.id, { ...layer }, false, false);
        },
        args.enableAdsorb,
    );

    framework.events.emitBaseOperate(layerId, BaseLayerViewType.ROTATE, args, {
        layerId,
        angle,
    });

    return layer.rotate;
}

/**
 * 移动图层
 */
export function updateLayerPosition(identity, id, x, y, originInfo, childOriginInfoMap, args: IBaseOperateFlag) {
    const empty = { left: 0, top: 0 };
    const framework = getFramework(identity);
    if (framework.operate.isDisableOperate()) return empty;
    const layer = framework.getLayer(id);
    if (!layer) return empty;

    const flatLayersMap = framework.getFlatLayersMap();
    const updatePosition = (layer: ILayerProps, originInfo: ILayerOriginInfo) => {
        const { children } = layer;
        (children || []).forEach((childLayer) => {
            const oldInfo = childOriginInfoMap.get(childLayer.id);
            if (!oldInfo) return;
            updatePosition(childLayer, oldInfo);
        });
        const { left, top } = originInfo;
        layer.left = ~~(left + x);
        layer.top = ~~(top + y);
        updateLayerTransform(identity, layer);
        flatLayersMap.set(layer.id, { ...layer }, args.reRender || false, args.reRender || false);
    };
    if (originInfo) {
        updatePosition(layer, originInfo);
    }

    framework.events.emitBaseOperate(id, BaseLayerViewType.MOVE, args, {
        layerId: id,
        x,
        y,
    });

    return {
        left: layer.left,
        top: layer.top,
    };
}

/**
 * 导出图层的原始信息
 *
 * @param identity - 标识 ID
 * @param info - 图层 ID 或 图层数据
 * @param fetchChildOriginInfoCb - 可选；获取子图层原始信息回调方法
 */
export function exportLayerOriginInfo(
    identity: string,
    info: string | ILayerProps,
    fetchChildOriginInfoCb?: (originInfo: ILayerOriginInfo) => void,
) {
    const isLayerId = isString(info);
    const coreAPI = CoreAPI(identity);
    const layer = isLayerId ? coreAPI.getLayer(info) : info;
    const childOriginInfoMap = new Map<string, ILayerOriginInfo>();

    if (!layer) {
        return {
            originInfo: undefined,
            childOriginInfoMap,
        };
    }
    const originInfo = getLayerOriginInfo(isLayerId ? layer.id : layer);

    function getLayerOriginInfo(args: string | ILayerProps) {
        const originLayer = isString(args) ? coreAPI.getLayer(args) : args;
        if (!originLayer) {
            return;
        }
        const { id, left, top, rotate, width, height, layerType, lock } = originLayer;
        const originInfo = { id, left, top, rotate, width, height, layerType, lock };
        const baseInfo = getBehavior(identity, originLayer)?.getActiveBaseInfo?.(originLayer);
        return Object.assign(
            originInfo,
            {
                pickId: '',
                cutWidth: LAYER_MIN_SIZE,
                cutHeight: LAYER_MIN_SIZE,
            },
            baseInfo,
        ) as ILayerOriginInfo;
    }

    function cloneInfo(layer: ILayerProps) {
        cloneChildren(layer.children);
        const originInfo = getLayerOriginInfo(isLayerId ? layer.id : layer);
        if (!originInfo) return;
        childOriginInfoMap.set(originInfo.id, originInfo);
        fetchChildOriginInfoCb?.(originInfo);
    }

    function cloneChildren(children: ILayerProps[]) {
        (children || []).forEach((item) => {
            cloneInfo(item);
        });
    }

    cloneChildren(layer.children);

    return {
        originInfo,
        childOriginInfoMap,
    };
}

/**
 * 旋转图层
 *
 * @param layer - 图层属性
 * @param activeInfo - 激活的图层信息
 * @param activeMap - 激活图层信息映射对象
 * @param angle - 旋转角度
 * @param callback - 可选；回调处理方法
 * @param enableAdsorb - 可选；是否开启吸附；默认值为 `false`
 */
export function execRotate(
    layer: ILayerProps,
    activeInfo: ILayerOriginInfo,
    activeMap: Map<string, ILayerOriginInfo>,
    angle: number,
    callback?: (layer: ILayerProps) => void,
    enableAdsorb?: boolean,
) {
    const cPoint = getCenterPointForOrigin(activeInfo);
    const updateRotate = (layer: ILayerProps, originInfo: ILayerOriginInfo, newPoint?: IPoint) => {
        const { rotate, width, height } = originInfo;
        const hostKeyDown = isHotKeyPress();
        layer.rotate = getActualAngle(rotate + angle);
        const isAdsorb = Math.abs(layer.rotate % 90) < 2 || Math.abs(layer.rotate % 90) > 88;
        if (hostKeyDown) {
            layer.rotate = Math.round(layer.rotate / 15) * 15;
        } else if (enableAdsorb && isAdsorb) {
            layer.rotate = Math.round(layer.rotate / 90) * 90;
        }
        if (newPoint) {
            layer.left = newPoint.x - width / 2;
            layer.top = newPoint.y - height / 2;
        }

        callback?.(layer);

        const { children } = layer;
        (children || []).forEach((item) => {
            const value = activeMap.get(item.id);
            if (!value) return;
            const sPoint = getCenterPointForOrigin(value);
            const rPoint = rotatePointWithFocus(sPoint, cPoint, angle);
            updateRotate(item, value, rPoint);
        });
    };
    updateRotate(layer, activeInfo);
}

/**
 * 旋转图层
 *
 * @param identity - 标识 ID
 * @param layer - 图层属性
 * @param angle - 旋转角度
 * @param callback - 可选；回调处理方法
 */
export function rotateLayer(
    identity: string,
    layer: ILayerProps,
    angle: number,
    callback?: (layer: ILayerProps) => void,
) {
    const { originInfo, childOriginInfoMap } = exportLayerOriginInfo(identity, layer);
    if (!originInfo) return;
    execRotate(layer, originInfo, childOriginInfoMap, angle, callback);
}

/**
 * 执行缩放操作
 *
 * @param identity - 标识 ID
 * @param layer - 图层属性
 * @param scaleInfo - 缩放信息
 * @param activeInfo - 激活的图层信息
 * @param activeMap - 激活图层信息映射对象
 * @param direction - 方向
 * @param reverse - 是否反转
 * @param isStart - 可选；是否开始
 * @param isFromAdjustScale - 可选；操作是否来自智能缩放
 * @param callback - 可选；回调处理方法
 */
export function execScale(
    identity: string,
    layer: ILayerProps,
    scaleInfo: ScaleInfo,
    activeInfo: ILayerOriginInfo,
    activeMap: Map<string, ILayerOriginInfo>,
    direction: DirectionType,
    reverse: boolean,
    isStart = false,
    isFromAdjustScale = false,
    callback?: (layer: ILayerProps) => void,
) {
    let widthRatio: number = 1;
    let heightRatio: number = 1;
    let parentOriginWidth: number = 0;
    let parentOriginHeight: number = 0;
    let parentOriginTop: number = 0;
    let parentOriginLeft: number = 0;
    let parentWidth: number = 0;
    let parentHeight: number = 0;
    let parentLeft: number = 0;
    let parentTop: number = 0;

    const updateScale = (
        layer: ILayerProps,
        originInfo: ILayerOriginInfo,
        previousTickLayer: ILayerProps,
        updateFlatLayer?: boolean,
    ) => {
        const { keepRatio = true } = scaleInfo;
        const { children } = layer;
        const { width: originWidth, height: originHeight, left: originLeft, top: originTop } = originInfo;

        let width = 0;
        let height = 0;
        let left = 0;
        let top = 0;
        let correctResult: ICorrectLayerMinSize | undefined;
        if (!updateFlatLayer) {
            const minSize = getLayerMinSize(identity, originInfo, layer);
            const sizeController = getScaleController(identity, originInfo, minSize, scaleInfo);

            const newSize = keepRatio ? sizeController.useWidth() : sizeController.useUnequalStretch();

            const customNextTickSize = getBehavior(identity, layer)?.getNextTickScaleSize?.(scaleInfo);
            if (customNextTickSize) {
                width = customNextTickSize.width;
                height = customNextTickSize.height;
            } else {
                width = newSize.width;
                height = newSize.height;
            }

            correctResult = getBehavior(identity, layer)?.correctLayerMinSize?.(
                width,
                height,
                newSize.ratio,
                originInfo,
            );
            if (correctResult) {
                width = correctResult.width;
                height = correctResult.height;
                scaleInfo.isMin = correctResult.isMin;
            }

            if (direction === DirectionType.CENTER) {
                left = originLeft + (originWidth - width) / 2;
                top = originTop + (originHeight - height) / 2;
            } else {
                const newCenter = getNewCenterPoint(originInfo, { width, height }, reverse, direction);
                left = newCenter.x - width / 2;
                top = newCenter.y - height / 2;
            }

            widthRatio = width / originWidth;
            heightRatio = height / originHeight;
            parentLeft = left;
            parentTop = top;
            parentWidth = width;
            parentHeight = height;
            parentOriginWidth = originWidth;
            parentOriginHeight = originHeight;
            parentOriginLeft = originLeft;
            parentOriginTop = originTop;
        } else {
            width = Math.ceil(widthRatio * originWidth);
            height = Math.ceil(heightRatio * originHeight);
            correctResult = getBehavior(identity, layer)?.correctLayerMinSize?.(width, height, widthRatio, originInfo);
            if (correctResult) {
                width = correctResult.width;
                height = correctResult.height;
            }
            left = ((originLeft - parentOriginLeft) / parentOriginWidth) * parentWidth + parentLeft;
            top = ((originTop - parentOriginTop) / parentOriginHeight) * parentHeight + parentTop;
        }

        layer.width = width;
        layer.height = height;
        layer.top = top;
        layer.left = left;

        getBehavior(identity, layer)?.onBeforeScaleUpdate?.(layer, {
            previousTickLayer,
            originInfo,
            correctResult,
            isStart,
            isFromAdjustScale,
        });

        (children || []).forEach((item, index) => {
            const value = activeMap.get(item.id);
            const previousTickItemLayer = previousTickLayer.children[index];
            if (!value) return;
            updateScale(item, value, previousTickItemLayer, true);
        });

        callback?.(layer);
    };

    const previousTickLayer = cloneDeep(layer);
    updateScale(layer, activeInfo, previousTickLayer);
}

/**
 * 缩放图层
 *
 * @param identity - 标识 ID
 * @param layer - 图层属性
 * @param offsetWidth - 偏移宽度
 * @param direction - 可选；方向；默认为正向
 * @param disableWithGroupHasMinLayer - 可选；组内有最小元素，是否停止缩放；默认值为 `true`
 * @param isFromAdjustScale - 可选；操作是否来自智能缩放
 * @param cb - 可选；回调处理方法
 * @param isStart - 可选；如果是连续的缩放, 比如拖拽缩放, 就需要传入是否是第一次
 */
export function scaleLayer(
    identity: string,
    layer: ILayerProps,
    offsetWidth: number,
    direction = DirectionType.POSITIVE,
    disableWithGroupHasMinLayer = true,
    isFromAdjustScale = false,
    callback?: (layer: ILayerProps) => void,
    isStart = false,
) {
    const { originInfo, childOriginInfoMap } = exportLayerOriginInfo(identity, layer);
    if (!originInfo) return;

    const flatLayersMap: Map<string, ILayerProps> = new Map();
    const flatLayer = (layer: ILayerProps) => {
        flatLayersMap.set(layer.id, layer);
        (layer.children || []).forEach((item) => flatLayer(item));
    };
    flatLayer(layer);

    let scaleInfo = { offset: offsetWidth, isMin: false };
    if (disableWithGroupHasMinLayer) {
        scaleInfo = getScaleInfo(identity, flatLayersMap, originInfo, childOriginInfoMap, offsetWidth);
    }

    execScale(
        identity,
        layer,
        scaleInfo,
        originInfo,
        childOriginInfoMap,
        direction,
        false,
        isStart,
        isFromAdjustScale,
        callback,
    );
}

/**
 * 调整缩放时的 offset
 *
 * @param identity - 标识 ID
 * @param flatLayers - 图层
 * @param layerOriginInfo - 图层源信息
 * @param childOriginInfoMap - 子图层源信息
 * @param offset - 偏移量
 */
export function getScaleInfo(
    identity: string,
    flatLayers: any,
    layerOriginInfo: ILayerOriginInfo,
    childOriginInfoMap: Map<string, ILayerOriginInfo>,
    offset: number,
) {
    // 是否达到组的最小值缩放值
    let isMin = false;
    if (offset < 0) {
        // 提取子图层到达临界尺寸的比例
        const ratioList: number[] = [];

        if (childOriginInfoMap.size) {
            childOriginInfoMap.forEach((value) => {
                const layer = flatLayers.get(value.id);
                if (layer) {
                    const ratio = getBehavior(identity, layer)?.getScaleMinRatio?.(value, layer);
                    if (ratio !== undefined) {
                        ratioList.push(ratio);
                    }
                }
            });

            // 调整偏移量
            if (ratioList.length) {
                const { width: aWidth } = layerOriginInfo;
                const maxRatio = Math.max(...ratioList);
                const res = maxRatio * aWidth - aWidth;
                offset = Math.max(res, offset);
                isMin = res === offset;
            }
        }
    }

    return { offset, isMin };
}

/**
 * 获取图层的最小尺寸
 *
 * @param identity - 标识 ID
 * @param originInfo - 图层源信息
 * @param layer - 图层属性
 */
export function getLayerMinSize(identity: string, originInfo: ILayerOriginInfo, layer: ILayerProps) {
    const { cutWidth, cutHeight } = originInfo;
    const framework = getFramework(identity);
    const originLayer = framework.getLayer(originInfo.id);
    const layerMinSize = getBehavior(identity, originLayer)?.getLayerMinSize?.(originInfo, layer);

    return Object.assign(
        {
            width: cutWidth,
            height: cutHeight,
        },
        layerMinSize,
    );
}

/**
 * 获取缩放控制器
 *
 * @param identity - 标识 ID
 * @param originInfo - 图层源信息
 * @param layerMinSize - 图层最小尺寸
 * @param scaleInfo - 缩放信息
 */
export function getScaleController(
    identity: string,
    originInfo: ILayerOriginInfo,
    layerMinSize: ISize,
    scaleInfo: { isMin: boolean; offset: number; offsetY?: number },
) {
    const framework = getFramework(identity);
    const layer = framework.getLayer(originInfo.id);
    const { width: originWidth, height: originHeight, layerType } = originInfo;
    const { width: minWidth, height: minHeight } = layerMinSize;
    const needLimitSizeLayer = getBehavior(identity, layer)?.getScaleIsNeedLimitSize?.();

    let ratio = 1;
    let width = 0;
    let height = 0;

    return {
        /**
         * 等比缩放
         **/
        useWidth() {
            width = originWidth + scaleInfo.offset;
            if (needLimitSizeLayer) {
                width = Math.max(width, minWidth);
            }
            ratio = width / originWidth;
            height = originHeight * ratio;
            if (needLimitSizeLayer) {
                height = Math.max(height, minHeight);
            }

            if (!scaleInfo.isMin && layerType !== LayerType.GROUP_LAYER) {
                scaleInfo.isMin = width <= minWidth || height <= minHeight;
            }

            if (scaleInfo.isMin) {
                const ratioHeight = (height - originHeight) / originHeight;
                scaleInfo.offset = ratioHeight * originWidth;
                width = originWidth + scaleInfo.offset;
            }

            return { ratio, width: Math.ceil(width), height: Math.ceil(height) };
        },
        /**
         * 不等比缩放
         **/
        useUnequalStretch() {
            width = originWidth + scaleInfo.offset;
            height = originHeight + (scaleInfo.offsetY || 0);

            if (needLimitSizeLayer) {
                width = Math.max(width, minWidth);
                height = Math.max(height, minHeight);
            }

            ratio = width / originWidth; // 即使在不等比缩放中，仍然保留宽度比例用于后续计算

            return { ratio, width: Math.ceil(width), height: Math.ceil(height) };
        },
    };
}

/**
 * 缩放图层组
 * 对应旧的 adjustLayerScale 方法
 *
 * @param identity
 * @param layers
 * @param oldSize
 * @param newSize
 * @param useHeightScale
 * @param resetPosition
 * @param isFromAdjustScale
 * @param callback
 */
export const scaleLayers = (
    identity: string,
    layers: ILayerProps[],
    oldSize: ISize,
    newSize: ISize,
    useHeightScale = false,
    resetPosition = true,
    isFromAdjustScale = false,
    callback?: (layer: ILayerProps) => void,
) => {
    let offsetWidth = newSize.width - oldSize.width;
    let finalHeight = 0;
    if (useHeightScale && resetPosition) {
        offsetWidth = oldSize.width * ((newSize.height - oldSize.height) / oldSize.height);
    }
    layers.forEach((layer) => {
        if (!layer || layer.parentId) return;
        const isBg = getBehavior(identity, layer)?.isBackgroundLayer?.();
        const isMain = getBehavior(identity, layer)?.isMainLayer?.();
        if (isBg || isMain) {
            const { width, height } = layer;
            const ratio = width / height;
            layer.width = newSize.width;
            finalHeight = Math.ceil(newSize.width / ratio);
            if (resetPosition) {
                layer.height = newSize.height;
            } else {
                layer.height = finalHeight;
            }
            getBehavior(identity, layer)?.adjustResize?.(layer, layer.width, layer.height);
            callback?.(layer);
        } else {
            const scaleGroupTempLayer = getGroupLayerProps({
                width: oldSize.width,
                height: oldSize.height,
                children: [layer],
            });
            scaleLayer(identity, scaleGroupTempLayer, offsetWidth, undefined, false, isFromAdjustScale, (layer) => {
                if (scaleGroupTempLayer.id !== layer.id) {
                    if (resetPosition) {
                        if (useHeightScale) {
                            const offsetLeft = newSize.width - oldSize.width / (oldSize.height / newSize.height);
                            layer.left += ~~(offsetLeft / 2);
                        } else {
                            const offsetTop = newSize.height - finalHeight;
                            layer.top += ~~(offsetTop / 2);
                        }
                    }
                    callback?.(layer);
                }
            });
        }
    });
};

/**
 * 调整图层组
 * 对应旧的 adjustLayerResize 方法
 *
 * @param identity
 * @param layers
 * @param oldSize
 * @param newSize
 * @param resetPosition
 */
export const resizeLayers = (
    identity: string,
    layers: ILayerProps[],
    oldSize: ISize,
    newSize: ISize,
    resetPosition = true,
) => {
    const offsetWidth = newSize.width - oldSize.width;
    const offsetHeight = newSize.height - oldSize.height;
    layers.forEach((layer) => {
        if (getBehavior(identity, layer)?.isBackgroundLayer?.() || getBehavior(identity, layer)?.isMainLayer?.()) {
            const newHeight = resetPosition ? newSize.height : layer.height;
            getBehavior(identity, layer)?.adjustResize?.(layer, newSize.width, newHeight);
        } else {
            if (layer.children) {
                resizeLayers(identity, layer.children, oldSize, newSize, resetPosition);
            }

            layer.left += ~~(offsetWidth / 2);
            if (resetPosition) {
                layer.top += ~~(offsetHeight / 2);
            }
        }
    });
};
